import { BlockEventType, BlockType, SortCriteria } from "@sperrdatei/dto";
import { getBlockSize } from "@sperrdatei/dto/utils";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";
import { BlockEventList } from "../../components/block-event-list";
import { Modal } from "../../components/modal";
import { Pagination } from "../../components/pagination";
import { isBlockEventHistoryItem } from "../../utils/is-block-event-history-event";
import { validateComment } from "../../utils/validate-comment";
import { validateNumberOrBlock } from "../../utils/validate-number-or-block";

import type {
  BlockEventHistory,
  FreeBlockEventSuccessResponse,
  GetPrefixRequestBody,
  GetPrefixSuccessResponse,
  GetRecentBlockEventsResponseSuccessResponse,
  UpdateBlockEventRequestBody,
  UpdateBlockEventSuccessResponse,
} from "@sperrdatei/dto/types";

export type EditNumberPageProps = object;

type ResultProps = {
  result: FreeBlockEventSuccessResponse | UpdateBlockEventSuccessResponse;
};

export const EditNumberPage: React.FC<EditNumberPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [number, setNumber] = useState("");
  const [numberError, setNumberError] = useState("");
  const [isPrivate, setIsPrivate] = useState<boolean>(true);
  const [blockType, setBlockType] = useState<BlockType>(BlockType.AllSurveys);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");
  const [result, setResult] = useState<
    FreeBlockEventSuccessResponse | UpdateBlockEventSuccessResponse | undefined
  >(undefined);
  const [blockEvents, setBlockEvents] = useState<BlockEventHistory[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [maxPage, setMaxPage] = useState(9999);
  const [showSortSelection, setShowSortSelection] = useState(false);
  const [sortBy, setSortby] = useState<SortCriteria>(SortCriteria.Date);

  const [selectedNumber, setSelectedNumber] = useState<
    BlockEventHistory | undefined
  >(undefined);
  const [selectedNumberForDeletion, setSelectedNumberForDeletion] = useState<
    BlockEventHistory | undefined
  >(undefined);

  useEffect(() => {
    apiFetch(`/api/block-event/institute/page/${currentPage}/${sortBy}`).then(
      (response) => {
        if (response.ok) {
          response
            .json()
            .then(
              ({
                events,
                pagination,
              }: GetRecentBlockEventsResponseSuccessResponse) => {
                setBlockEvents(events);
                setMaxPage(pagination.pages);
              },
            );
        }
      },
    );
  }, [apiFetch, isLoading, currentPage, sortBy]);

  const DeleteBlockEventModal: React.FC = () => {
    const isNumber =
      selectedNumberForDeletion && selectedNumberForDeletion.blockSize === 1;
    const fullNumber = selectedNumberForDeletion
      ? `${selectedNumberForDeletion.prefix}-${selectedNumberForDeletion.number}`
      : "";
    const id = selectedNumberForDeletion ? selectedNumberForDeletion.id : -1;
    return (
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isNumber ? `Sperrung freigeben` : `Nummer Freigeben`}
        primaryAction={{
          label: "Freigeben",
          buttonStyle: "danger",
          showLoadingIndicator: isLoading,
          onClick: async () => {
            setIsLoading(true);
            const response = await apiFetch(`/api/block-event/${id}`, {
              method: "DELETE",
            });
            setIsLoading(false);

            if (response.ok) {
              setResult(await response.json());
              setIsModalOpen(false);
              return;
            }

            navigate("/error");
            return;
          },
        }}
        secondaryAction={{
          label: "Abbrechen",
          onClick: () => setIsModalOpen(false),
        }}
      >
        <p>
          Sind Sie sicher das sie{" "}
          {isNumber ? <span>die Nummer</span> : <span>den Rufnummerblock</span>}{" "}
          <strong>{fullNumber}</strong> wieder freigeben wollen?
        </p>
      </Modal>
    );
  };

  const editBlockEvent = async (): Promise<void> => {
    setNumberError("");

    const numberValidationresult = validateNumberOrBlock(number, isPrivate);
    if (!numberValidationresult.isValid) {
      setNumberError(numberValidationresult.errorMessage);
      return;
    }

    const commentValidationResult = validateComment(comment, number);
    if (!commentValidationResult.isValid) {
      setCommentError(commentValidationResult.errorMessage);
      return;
    }

    if (!selectedNumber) {
      return;
    }

    const getPrefixBody: GetPrefixRequestBody = { number };
    const getPrefixResponse = await apiFetch("/api/number/prefix", {
      method: "POST",
      body: JSON.stringify(getPrefixBody),
    });

    if (!getPrefixResponse.ok) {
      setNumberError("Die Vorwahl ist ungültig.");
      return;
    }

    setIsLoading(true);

    const { prefix } =
      (await getPrefixResponse.json()) as GetPrefixSuccessResponse;
    const numberWithoutPrefix = number.replace(prefix, "");

    const body: UpdateBlockEventRequestBody = {
      id: selectedNumber.id,
      updatedValues: {
        prefix: prefix,
        number: numberWithoutPrefix,
        comment: comment,
        isPrivate: isPrivate,
        blockType: blockType,
        eventType: BlockEventType.Block,
        blockSize: getBlockSize(numberWithoutPrefix),
      },
    };

    const response = await apiFetch("/api/block-event", {
      method: "PATCH",
      body: JSON.stringify(body),
    });

    if (response.ok) {
      setResult(await response.json());
      return;
    }

    setIsLoading(false);
    navigate("/error");
  };

  const clearForm = (): void => {
    setNumber("");
    setBlockType(BlockType.AllSurveys);
    setIsPrivate(true);
    setComment("");
    setResult(undefined);
    setSelectedNumber(undefined);
    setSelectedNumberForDeletion(undefined);
  };

  const Result: React.FC<ResultProps> = ({ result }: ResultProps) => {
    if ("inserted" in result) {
      return (
        <>
          <h6>Bearbeitung abgeschlossen</h6>
          <p>
            <strong>Ergebnis</strong>: <br />
            <span>
              Es {result.inserted === 1 ? "wurde" : "wurden"}{" "}
              <strong>{result.inserted}</strong> neue{" "}
              {result.inserted === 1 ? "Sperrnummer" : "Sperrnummern"}{" "}
              eingetragen und <strong>{result.deleted}</strong>{" "}
              {result.deleted === 1 ? "Nummer wurde" : "Nummern wurden"} wieder
              freigegeben.
            </span>
          </p>
          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={() => clearForm()}
          >
            OK
          </button>
        </>
      );
    }

    return (
      <>
        <h6>Freigabe abgeschlossen</h6>
        <p>
          <strong>Ergebnis</strong>: <br />
          <span>
            <strong>{result.freed}</strong>{" "}
            {result.freed > 1 ? "Nummern wurden" : "Nummer wurde"} wieder
            freigegeben.
          </span>
        </p>
        <button
          type="button"
          className="btn btn-primary mb-3"
          onClick={() => clearForm()}
        >
          OK
        </button>
      </>
    );
  };

  return (
    <div className="container ms-0">
      <DeleteBlockEventModal />
      <h3 className="page-caption mb-3">Nummer bearbeiten</h3>
      {result ? (
        <Result result={result} />
      ) : (
        <>
          <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
            <p>
              Hier können Sie Rufnummern und Rufnummernblöcke bearbeiten, die
              Ihr Institut in den letzten 7 Tagen erfasst hat.
            </p>
          </div>

          <div className="input-group mb-3">
            <span className="input-group-text">Sortierung</span>
            <button
              className="btn btn-outline-secondary border-secondary-subtle dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              onClick={() => setShowSortSelection(true)}
            >
              {sortBy === SortCriteria.Date ? "Datum" : "Nummer"}
            </button>
            <ul
              className={
                showSortSelection ? "dropdown-menu show" : "dropdown-menu"
              }
              style={{ transform: "translate(103px, 40px)" }}
            >
              <li>
                <span
                  className="dropdown-item"
                  onClick={() => {
                    setSortby(SortCriteria.Date);
                    setShowSortSelection(false);
                  }}
                >
                  Datum
                </span>
              </li>
              <li>
                <span
                  className="dropdown-item"
                  onClick={() => {
                    setSortby(SortCriteria.Number);
                    setShowSortSelection(false);
                  }}
                >
                  Nummer
                </span>
              </li>
            </ul>
          </div>

          {selectedNumber ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                editBlockEvent();
              }}
              className="container"
              noValidate
            >
              <div className="row">
                <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 mt-0 p-0">
                  <div className="mb-1">
                    <label htmlFor="number" className="form-text">
                      <strong>Nummer</strong>
                    </label>
                    <input
                      type="text"
                      id="number"
                      value={number}
                      maxLength={15}
                      className={
                        numberError
                          ? "form-control is-invalid"
                          : "form-control "
                      }
                      onChange={(e) => setNumber(e.target.value)}
                      onInput={(e) => {
                        e.currentTarget.value = e.currentTarget.value.replace(
                          /[^0-9x*]/g,
                          "",
                        );
                      }}
                      required
                    />
                    <div id="numberFeedback" className="invalid-feedback">
                      {numberError}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <p className="col form-text mb-1 p-0">
                  <strong>Art der Sperrung</strong>
                </p>
              </div>
              <div className="row">
                <button
                  type="button"
                  className={
                    isPrivate
                      ? "btn mb-1 border-white btn-primary col-xl-2 col-lg-3 col-12"
                      : "btn mb-1 border-white btn-secondary col-xl-2 col-lg-3 col-12"
                  }
                  onClick={() => {
                    setBlockType(BlockType.AllSurveys);
                    setIsPrivate(true);
                  }}
                >
                  Privat
                  <small
                    className="text-muted"
                    style={{ display: "block", whiteSpace: "nowrap" }}
                  >
                    Alle Befragungen
                  </small>
                </button>
                <button
                  type="button"
                  className={
                    !isPrivate && blockType === BlockType.AllSurveys
                      ? "btn mb-1 border-white btn-primary col-xl-2 col-lg-3 col-12"
                      : "btn mb-1 border-white btn-secondary col-xl-2 col-lg-3 col-12"
                  }
                  onClick={() => {
                    setBlockType(BlockType.AllSurveys);
                    setIsPrivate(false);
                  }}
                >
                  Gewerblich
                  <small
                    className="text-muted"
                    style={{ display: "block", whiteSpace: "nowrap" }}
                  >
                    Alle Befragungen
                  </small>
                </button>
                <button
                  type="button"
                  className={
                    !isPrivate && blockType === BlockType.PopulationSurveys
                      ? "btn border-white btn-primary mb-1 col-xl-3 col-lg-4"
                      : "btn border-white btn-secondary mb-1 col-xl-3 col-lg-4"
                  }
                  onClick={() => {
                    setBlockType(BlockType.PopulationSurveys);
                    setIsPrivate(false);
                  }}
                >
                  Gewerblich
                  <small style={{ display: "block" }}>
                    Bevölkerungsbefragungen
                  </small>
                </button>
              </div>
              <div className="row">
                <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0">
                  <label htmlFor="comment" className="form-text">
                    <strong>Bemerkung</strong>
                  </label>
                  <textarea
                    className={
                      commentError ? "form-control is-invalid" : "form-control"
                    }
                    id="comment"
                    rows={1}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={getBlockSize(number) < 100}
                  ></textarea>
                  <div id="commentFeedback" className="invalid-feedback">
                    {commentError}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0 d-flex justify-content-between mt-3">
                  <button
                    type="button"
                    className="btn btn-secondary mb-3"
                    onClick={() => clearForm()}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary mb-3"
                    onClick={() => editBlockEvent()}
                  >
                    {isLoading ? (
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        aria-hidden="true"
                      ></span>
                    ) : null}
                    Bearbeiten
                  </button>
                </div>
              </div>
            </form>
          ) : null}
          {!selectedNumber && blockEvents.length ? (
            <div className="col-xxl-7 col-xl-7 col-lg-10 col-xs-12 p-0">
              <BlockEventList
                events={blockEvents}
                hideInstitute={true}
                selectEvent={(event) => {
                  if (isBlockEventHistoryItem(event)) {
                    setSelectedNumber(event);
                    setNumber(event.prefix + event.number);
                    setIsPrivate(event.isPrivate);
                    setComment(event.comment);
                  }
                }}
                deleteEvent={(event) => {
                  if (isBlockEventHistoryItem(event)) {
                    setSelectedNumberForDeletion(event);
                    setIsModalOpen(true);
                  }
                }}
              />
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                maxPage={maxPage}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};
